import type { CrmTranslate } from './crm-copy';

export function createCrmPipelineCopy(t: CrmTranslate) {
  return {
    kanbanBoard: {
      fallbacks: {
        unnamedContact: t('crm.kanbanBoard.fallbacks.unnamedContact'),
      },
      actions: {
        removeFromPipeline: t('crm.kanbanBoard.actions.removeFromPipeline'),
        removeContactFromPipeline: (name: string) =>
          t('crm.kanbanBoard.actions.removeContactFromPipeline', {
            values: { name },
          }),
        editStageColor: t('crm.kanbanBoard.actions.editStageColor'),
        editColorForStage: (stage: string) =>
          t('crm.kanbanBoard.actions.editColorForStage', {
            values: { stage },
          }),
        editStageName: t('crm.kanbanBoard.actions.editStageName'),
        renameStage: (stage: string) =>
          t('crm.kanbanBoard.actions.renameStage', {
            values: { stage },
          }),
        loading: t('crm.kanbanBoard.actions.loading'),
        loadMore: (remaining: number) =>
          t('crm.kanbanBoard.actions.loadMore', {
            values: { remaining },
          }),
        retry: t('crm.kanbanBoard.actions.retry'),
      },
      labels: {
        draggablePipelineCard: t('crm.kanbanBoard.labels.draggablePipelineCard'),
        cardInStage: (name: string, stage: string) =>
          t('crm.kanbanBoard.labels.cardInStage', {
            values: { name, stage },
          }),
        stageGroup: (stage: string, visible: number, total: number) =>
          t('crm.kanbanBoard.labels.stageGroup', {
            values: { stage, visible, total },
          }),
        stageName: t('crm.kanbanBoard.labels.stageName'),
      },
      states: {
        dropContactsHere: t('crm.kanbanBoard.states.dropContactsHere'),
        contactsLoading: t('crm.kanbanBoard.states.contactsLoading'),
        noPipelineData: t('crm.kanbanBoard.states.noPipelineData'),
      },
      errors: {
        unknownError: t('crm.kanbanBoard.errors.unknownError'),
        loadingPipeline: (message: string) =>
          t('crm.kanbanBoard.errors.loadingPipeline', {
            values: { message },
          }),
      },
    },
    dealsKanbanBoard: {
      actions: {
        editStageColor: t('crm.dealsKanbanBoard.actions.editStageColor'),
        editColorForStage: (stage: string) =>
          t('crm.dealsKanbanBoard.actions.editColorForStage', {
            values: { stage },
          }),
        editStageName: t('crm.dealsKanbanBoard.actions.editStageName'),
        renameStage: (stage: string) =>
          t('crm.dealsKanbanBoard.actions.renameStage', {
            values: { stage },
          }),
        loading: t('crm.dealsKanbanBoard.actions.loading'),
        loadMore: (remaining: number) =>
          t('crm.dealsKanbanBoard.actions.loadMore', {
            values: { remaining },
          }),
        retry: t('crm.dealsKanbanBoard.actions.retry'),
      },
      labels: {
        draggablePipelineCard: t('crm.dealsKanbanBoard.labels.draggablePipelineCard'),
        cardInStage: (name: string, stage: string) =>
          t('crm.dealsKanbanBoard.labels.cardInStage', {
            values: { name, stage },
          }),
        stageGroup: (stage: string, visible: number, total: number) =>
          t('crm.dealsKanbanBoard.labels.stageGroup', {
            values: { stage, visible, total },
          }),
        stageCount: (visible: number, total: number) =>
          t('crm.dealsKanbanBoard.labels.stageCount', {
            values: { visible, total },
          }),
        stageName: t('crm.dealsKanbanBoard.labels.stageName'),
      },
      states: {
        dropDealsHere: t('crm.dealsKanbanBoard.states.dropDealsHere'),
        dealsLoading: t('crm.dealsKanbanBoard.states.dealsLoading'),
        noDealsData: t('crm.dealsKanbanBoard.states.noDealsData'),
      },
      errors: {
        unknownError: t('crm.dealsKanbanBoard.errors.unknownError'),
        loadingDeals: (message: string) =>
          t('crm.dealsKanbanBoard.errors.loadingDeals', {
            values: { message },
          }),
      },
    },
    savedViewsBar: {
      actions: {
        updateCurrentView: t('crm.savedViewsBar.actions.updateCurrentView'),
        deleteView: t('crm.savedViewsBar.actions.deleteView'),
        saveCurrentView: t('crm.savedViewsBar.actions.saveCurrentView'),
        save: t('crm.savedViewsBar.actions.save'),
        cancel: t('crm.savedViewsBar.actions.cancel'),
      },
      labels: {
        view: t('crm.savedViewsBar.labels.view'),
        saveCurrentViewAs: t('crm.savedViewsBar.labels.saveCurrentViewAs'),
      },
      options: {
        default: t('crm.savedViewsBar.options.default'),
      },
      placeholders: {
        viewName: t('crm.savedViewsBar.placeholders.viewName'),
      },
      messages: {
        created: t('crm.savedViewsBar.messages.created'),
        updated: t('crm.savedViewsBar.messages.updated'),
        deleted: t('crm.savedViewsBar.messages.deleted'),
      },
      errors: {
        nameRequired: t('crm.savedViewsBar.errors.nameRequired'),
        createFailed: t('crm.savedViewsBar.errors.createFailed'),
        updateFailed: t('crm.savedViewsBar.errors.updateFailed'),
        deleteFailed: t('crm.savedViewsBar.errors.deleteFailed'),
      },
    },
    pipelinePage: {
      title: t('crm.pipelinePage.title'),
      entityTypes: {
        contacts: t('crm.pipelinePage.entityTypes.contacts'),
        deals: t('crm.pipelinePage.entityTypes.deals'),
      },
      actions: {
        retry: t('crm.pipelinePage.actions.retry'),
      },
      states: {
        errorTitle: t('crm.pipelinePage.states.errorTitle'),
        emptyTitle: t('crm.pipelinePage.states.emptyTitle'),
        emptyDescription: t('crm.pipelinePage.states.emptyDescription'),
        selectPipeline: (entityType: string) =>
          t('crm.pipelinePage.states.selectPipeline', {
            values: { entityType },
          }),
      },
      messages: {
        stageColorUpdated: t('crm.pipelinePage.messages.stageColorUpdated'),
        stageNameUpdated: t('crm.pipelinePage.messages.stageNameUpdated'),
      },
      errors: {
        stageColorUpdateFailed: t('crm.pipelinePage.errors.stageColorUpdateFailed'),
        stageNameUpdateFailed: t('crm.pipelinePage.errors.stageNameUpdateFailed'),
      },
    },
    conversationPanel: {
      title: t('crm.conversationPanel.title'),
      emptyDescription: t('crm.conversationPanel.emptyDescription'),
      actions: {
        refresh: t('crm.conversationPanel.actions.refresh'),
        createConversation: t('crm.conversationPanel.actions.createConversation'),
        creating: t('crm.conversationPanel.actions.creating'),
        sendMessage: t('crm.conversationPanel.actions.sendMessage'),
        sending: t('crm.conversationPanel.actions.sending'),
      },
      labels: {
        subject: t('crm.conversationPanel.labels.subject'),
        initialMessage: t('crm.conversationPanel.labels.initialMessage'),
        thread: t('crm.conversationPanel.labels.thread'),
        statusUpdated: (status: string, date: string) =>
          t('crm.conversationPanel.labels.statusUpdated', {
            values: { status, date },
          }),
      },
      placeholders: {
        threadContext: t('crm.conversationPanel.placeholders.threadContext'),
        composer: t('crm.conversationPanel.placeholders.composer'),
      },
      states: {
        loading: t('crm.conversationPanel.states.loading'),
        noMessages: t('crm.conversationPanel.states.noMessages'),
      },
      messageDirections: {
        sent: t('crm.conversationPanel.messageDirections.sent'),
        received: t('crm.conversationPanel.messageDirections.received'),
      },
      messages: {
        conversationCreated: t('crm.conversationPanel.messages.conversationCreated'),
        messageAdded: t('crm.conversationPanel.messages.messageAdded'),
      },
      errors: {
        loadThreadsFailed: t('crm.conversationPanel.errors.loadThreadsFailed'),
        loadConversationFailed: t('crm.conversationPanel.errors.loadConversationFailed'),
        subjectRequired: t('crm.conversationPanel.errors.subjectRequired'),
        createConversationFailed: t('crm.conversationPanel.errors.createConversationFailed'),
        sendMessageFailed: t('crm.conversationPanel.errors.sendMessageFailed'),
      },
      fallbacks: {
        status: t('crm.conversationPanel.fallbacks.status'),
      },
    },
  };
}
